import { createApi } from "@reduxjs/toolkit/query/react";
import { queryParams } from "../../../utils/helper";
import { baseQueryWithAuth } from "../../BaseQueryWithAuth/baseQueryWithAuth";

export const courseApiService = createApi({
  reducerPath: "courseApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Course", "Media"],
  endpoints: (builder) => ({
    createCourse: builder.mutation({
      query: ({ payload, params }) => {
        let query = queryParams(params);
        return {
          url: `course/create${query}`,
          method: "POST",
          body: payload,
        };
      },
    }),
    updateCourse: builder.mutation({
      query: ({ payload, params }) => {
        let query = queryParams(params);
        return {
          url: `course/update/${payload._id}${query}`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: (result, error, { payload }) => [
        { type: "Course", id: payload._id }, // specific course
        { type: "Course", id: "DRAFT_LIST" },
        { type: "Course", id: "TEMPLATE_PURCHASED_LIST" },
      ],
    }),
    buyCourse: builder.mutation({
      query: ({ payload, params }) => {
        let query = queryParams(params);
        return {
          url: `course/buy${query}`,
          method: "POST",
          body: payload,
        };
      },
    }),
    getCourse: builder.query({
      query: (data) => {
        const { id, ...params } = data || {};
        return {
          url: id ? `course/get/${id}` : `course/get`,
          method: "GET",
          params,
        };
      },
      providesTags: (result, error, data) =>
        data?.id
          ? [{ type: "Course", id: data.id }]
          : [{ type: "Course", id: "LIST" }],
      keepUnusedDataFor: 0,
    }),

    getCourseById: builder.query({
      query: ({ id, lang }) => ({
        url: `course/get/${id}`,
        method: "GET",
        params: { lang },
      }),
      providesTags: (result, error, id) => [{ type: "Course", id }],
      keepUnusedDataFor: 0,
    }),
    getCoursesByCategory: builder.query({
      query: (data) => {
        let query = queryParams(data);
        return `course/get-by-category${query}`;
      },
      keepUnusedDataFor: 0,
    }),
    getMyList: builder.query({
      query: (data) => ({
        url: `course/get-my-list`,
        method: "GET",
        params: data,
      }),
      providesTags: ["MyList"],
      keepUnusedDataFor: 0,
    }),
    getMedia: builder.query({
      query: ({ courseId, mediaId, learnerId }) => ({
        url: `course/getmedia`,
        method: "GET",
        params: { courseId, mediaId, learnerId },
      }),
      providesTags: (result, error, { courseId, mediaId }) => [
        { type: "Media", id: `${courseId}-${mediaId}` },
      ],
      keepUnusedDataFor: 0,
    }),
    getAllTrackedMedia: builder.query({
      query: ({ courseId, params }) => ({
        url: `course/getall/${courseId}`,
        method: "GET",
        params,
      }),
      providesTags: (result, error, id) => [{ type: "GetAllTrackedMedia", id }],
      keepUnusedDataFor: 0,
    }),
    getCourseProgress: builder.query({
      query: ({ courseId, params }) => ({
        url: `course/get/progress/${courseId}`,
        method: "GET",
        params,
      }),
      providesTags: (result, error, id) => [{ type: "GetCourseProgress", id }],
      keepUnusedDataFor: 0,
    }),
    trackCourse: builder.mutation({
      query: ({ payload, params }) => {
        let query = queryParams(params);
        return {
          url: `course/track-course${query}`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: (result, error, { courseid }) => [
        { type: "GetAllTrackedMedia", id: courseid },
        { type: "GetCourseProgress", id: courseid },
      ],
    }),
    getDraftCourse: builder.query({
      query: ({ status, template_status, params }) => {
        return {
          url: `course/status/${status}/${template_status}`,
          method: "GET",
          params,
        };
      },
      providesTags: [{ type: "Course", id: "DRAFT_LIST" }],
    }),
    getTemplatePurchasedCourse: builder.query({
      query: (data) => {
        let query = queryParams(data);
        return {
          url: `course/get/subscription${query}`,
          method: "GET",
        };
      },
      providesTags: [{ type: "Course", id: "TEMPLATE_PURCHASED_LIST" }],
    }),
    updateCourseStatus: builder.mutation({
      query: ({ id, body, params }) => {
        let query = queryParams(params);
        return {
          url: `course/handle-status/${id}${query}`,
          method: "PUT",
          body,
        };
      },
    }),
    updatePublishDeleteStatus: builder.mutation({
      query: ({ id, body, params }) => {
        let query = queryParams(params);
        return {
          url: `course/publish/${id}${query}`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: ["MyList"],
    }),
    checkPublishAcsess: builder.mutation({
      query: ({ payload, params }) => {
        let query = queryParams(params);
        return {
          url: `course/check-publish${query}`,
          method: "POST",
          body: payload,
        };
      },
    }),
    getHomeCourse: builder.query({
      query: (data) => {
        let query = queryParams(data);
        return {
          url: `course/get/all${query}`,
          method: "GET",
        };
      },
    }),
  }),
});

export const {
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useBuyCourseMutation,
  useGetCourseQuery,
  useGetCourseByIdQuery,
  useGetCoursesByCategoryQuery,
  useGetMyListQuery,
  useGetMediaQuery,
  useGetAllTrackedMediaQuery,
  useTrackCourseMutation,
  useGetDraftCourseQuery,
  useGetTemplatePurchasedCourseQuery,
  useUpdateCourseStatusMutation,
  useGetCourseProgressQuery,
  useUpdatePublishDeleteStatusMutation,
  useCheckPublishAcsessMutation,
  useGetHomeCourseQuery,
} = courseApiService;
